import React from "react";
import { useParams } from "react-router-dom";
import { StackSection, Columns, Column, Box } from "@origin-digital/ods-core";
import { useCategoryArticles } from "../../hooks";
import { ContentSection } from "./layout/ContentSection/ContentSection";
import { PopularArticlesSection } from "./layout/PopularArticlesSection/PopularArticlesSection";
import { QuickActionsSection } from "./layout/QuickActionsSection/QuickActionsSection";
import { ArticlesSection } from "./layout/ArticlesSection/ArticlesSection";
import type { CategoryChild } from "../../services/categories/types";
import { ContactUs } from "../../components/ContactUs/ContactUs";
import { NeedHelp } from "../../components/NeedHelp";
import styled from "styled-components";
import { PageTemplate } from "../PageTemplate/PageTemplate";

const EMPTY_SUBCATEGORIES: CategoryChild[] = [];
const MAX_POPULAR_ARTICLES = 3;

const OverlapBox = styled(Box)`
  /* ODS Columns defaults to align-items: flex-start — override to stretch
     both columns to equal height regardless of content differences */
  & [data-id="columns"] {
    align-items: stretch;
  }
`;

export const CategoryPage: React.FC = () => {
  const { categoryUri } = useParams<{ categoryUri: string }>();
  const { data, loading, error } = useCategoryArticles(categoryUri);
  const category = data?.category || null;

  const quickActions = category?.quickActions ?? [];
  const popularArticles =
    category?.subLinks?.slice(0, MAX_POPULAR_ARTICLES) ?? [];
  const hasQuickActions = loading || quickActions.length > 0;
  const hasPopularArticles = loading || popularArticles.length > 0;

  const renderOverlapContent = () => {
    if (!hasPopularArticles && !hasQuickActions) return null;
    if (hasPopularArticles && hasQuickActions) {
      return (
        <Columns space={{ xs: "medium", lg: "large" }} collapseBelow="sm">
          <Column width={{ xs: "content", lg: "2/3" }}>
            <PopularArticlesSection
              popularArticles={popularArticles}
              isLoading={loading}
            />
          </Column>
          <Column width={{ xs: "content", lg: "1/3" }}>
            <QuickActionsSection
              quickActions={quickActions}
              loading={loading}
            />
          </Column>
        </Columns>
      );
    }
    if (hasQuickActions) {
      return (
        <QuickActionsSection
          quickActions={quickActions}
          loading={loading}
          horizontal
        />
      );
    }
    return (
      <PopularArticlesSection
        popularArticles={popularArticles}
        isLoading={loading}
      />
    );
  };

  return (
    <PageTemplate
      data-id="help-category-page"
      metadata={{ title: category?.name, description: category?.description }}
    >
      <StackSection
        hideGutter={{ xs: true, sm: false }}
        data-id="category-page"
        width="xlarge"
        space="none"
        paddingY="none"
        backgroundColor="cardBackground"
      >
        <ContentSection
          breadcrumbs={category?.breadcrumbs || []}
          description={category?.description || ""}
          imageUrl={category?.image || ""}
          isLoading={loading}
          name={category?.name || ""}
        />

        <OverlapBox marginTop="medium">{renderOverlapContent()}</OverlapBox>
        <ArticlesSection
          subcategories={data?.category.children ?? EMPTY_SUBCATEGORIES}
          loading={loading}
          error={error}
        />
        <Box marginBottom={{ xs: "xxlarge", lg: "xxxlarge" }}>
          <NeedHelp loginOnly />
        </Box>
      </StackSection>
    </PageTemplate>
  );
};
